import React, { useState, useRef, useEffect } from 'react';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { useLocation } from 'react-router-dom';
import { usePermissions } from '../../context/permissionContext';

// Material-UI Icons (v4 compatible)
import ExtensionIcon from '@material-ui/icons/Extension';
import CodeIcon from '@material-ui/icons/Code';
import WorkIcon from '@material-ui/icons/Work';
import DashboardIcon from '@material-ui/icons/AccountTree';
// import DashboardIcon from '@material-ui/icons/Memory';

import { Palette, Puzzle, ArrowRightLeft, CircleArrowUp, Shield, BarChart2, Sparkles, Cloud, FileText } from 'lucide-react';

import './menu.scss';

export const MainListItems = () => {
  const location = useLocation();
  const { hasPermission } = usePermissions();
  
  return (
    <div>
      {/* Features / Product Overview - always visible */}
      <a href="/features">
        <ListItem button className={location.pathname === '/features' ? 'menu-active' : 'menu-inactive'}>
          <ListItemIcon>
            <Sparkles size={20} />
          </ListItemIcon>
          <ListItemText primary="Features" />
        </ListItem>
      </a>

      {/* Connected Apps - Only show if user has permission */}
      {hasPermission('connectedApps') && (
        <a href="/settings" data-menu-tour="connectedApps">
          <ListItem button className={location.pathname === '/settings' ? 'menu-active' : 'menu-inactive'}>
            <ListItemIcon>
              <ExtensionIcon />
            </ListItemIcon>
            <ListItemText primary="Connected Apps" />
          </ListItem>
        </a>
      )}
      
      {/* Super Admin - Only show for platform super admins */}
      {hasPermission('superAdmin') && (
        <a href="/superAdmin">
          <ListItem button className={location.pathname === '/superAdmin' ? 'menu-active' : 'menu-inactive'}>
            <ListItemIcon>
              <BarChart2 size={20} />
            </ListItemIcon>
            <ListItemText primary="Super Admin" />
          </ListItem>
        </a>
      )}

      {/* Logs - Super admin only: session logs + audit logs / token usage */}
      {hasPermission('superAdmin') && (
        <a href="/sessionLogs">
          <ListItem button className={location.pathname === '/sessionLogs' ? 'menu-active' : 'menu-inactive'}>
            <ListItemIcon>
              <FileText size={20} />
            </ListItemIcon>
            <ListItemText primary="Logs" />
          </ListItem>
        </a>
      )}

      {/* Tenant Admin - Only show if user has permission */}
      {hasPermission('tenantAdmin') && (
        <a href="/settings/cloud-accounts/aws">
          <ListItem button className={location.pathname === '/settings/cloud-accounts/aws' ? 'menu-active' : 'menu-inactive'}>
            <ListItemIcon><Cloud size={20} /></ListItemIcon>
            <ListItemText primary="Cloud Accounts" />
          </ListItem>
        </a>
      )}

      {hasPermission('tenantAdmin') && (
        <a href="/tenantAdmin">
          <ListItem button className={location.pathname === '/tenantAdmin' ? 'menu-active' : 'menu-inactive'}>
            <ListItemIcon>
              <BarChart2 size={20} />
            </ListItemIcon>
            <ListItemText primary="Tenant Admin" />
          </ListItem>
        </a>
      )}

      {/* API Templates - Only show if user has permission */}
      {/* {hasPermission('apiTemplates') && (
        <a href="/templates">
          <ListItem button className={location.pathname === '/templates' ? 'menu-active' : 'menu-inactive'}>
            <ListItemIcon>
              <CodeIcon />
            </ListItemIcon>
            <ListItemText primary="API Templates" />
          </ListItem>
        </a>
      )} */}
    </div>
  );
};

const MENU_TOUR_KEY = 'menu_tour_dismissed';

const MENU_TOUR_STEPS = [
  { key: 'connectedApps', title: 'Connected Apps', body: 'Start here — connect your GitHub, Confluence, Azure, AWS and other enterprise systems so Intelog can access your repos and infrastructure.', perm: 'connectedApps' },
  { key: 'projectCenter', title: 'Project Centre', body: 'View and manage all your generated integration projects in one place. Track status, open files and re-deploy from here.', perm: 'projectCenter' },
  { key: 'smartHub',      title: 'Smart Hub', body: 'Design, generate and deploy production-grade integrations. Use New Integrations to build from scratch or Enhance Existing to analyse and improve existing codebases.', perm: null },
];

function MenuTour({ projectCenterRef, smartHubRef, onDismiss }) {
  const [step, setStep] = React.useState(0);
  const [rect, setRect] = React.useState(null);

  const getEl = React.useCallback((key) => {
    if (key === 'connectedApps') return document.querySelector('[data-menu-tour="connectedApps"]');
    if (key === 'projectCenter')  return projectCenterRef?.current;
    if (key === 'smartHub')       return smartHubRef?.current;
    return null;
  }, [projectCenterRef, smartHubRef]);

  React.useEffect(() => {
    let raf;
    const measure = () => {
      const current = MENU_TOUR_STEPS[step];
      const el = getEl(current?.key);
      if (!el) { setStep(s => Math.min(s + 1, MENU_TOUR_STEPS.length - 1)); return; }
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) { setStep(s => Math.min(s + 1, MENU_TOUR_STEPS.length - 1)); return; }
      setRect({ top: r.top, left: r.right + 12, height: r.height });
    };
    raf = requestAnimationFrame(() => requestAnimationFrame(measure));
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); };
  }, [step, getEl]);

  const handleNext = () => {
    if (step >= MENU_TOUR_STEPS.length - 1) { onDismiss(); }
    else { setStep(s => s + 1); }
  };

  if (!rect) return null;
  const current = MENU_TOUR_STEPS[step];
  const total = MENU_TOUR_STEPS.length;
  const isLast = step === total - 1;

  return (
    <>
      <style>{`@keyframes menuTourIn { from { opacity:0; transform:translateX(-8px); } to { opacity:1; transform:translateX(0); } }`}</style>
      <div style={{
        position: 'fixed',
        top: Math.max(8, rect.top + rect.height / 2 - 70),
        left: rect.left,
        width: 260,
        maxWidth: 260,
        zIndex: 9999,
        background: '#0e1421',
        border: '1px solid rgba(59,127,255,0.4)',
        borderRadius: 12,
        padding: '14px 16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        animation: 'menuTourIn 0.2s ease',
        userSelect: 'none',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}>
        {/* Arrow pointing left */}
        <div style={{ position: 'absolute', left: -8, top: '50%', transform: 'translateY(-50%)', width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderRight: '8px solid rgba(59,127,255,0.4)' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#3b7fff', fontFamily: 'monospace' }}>{step + 1} / {total}</span>
          <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: '#3d5273', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#dce8ff', marginBottom: 6, whiteSpace: 'normal', wordBreak: 'break-word' }}>{current.title}</div>
        <div style={{ fontSize: 12, color: '#7a93bc', lineHeight: 1.6, marginBottom: 14, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{current.body}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: '#3d5273', fontSize: 11, cursor: 'pointer', padding: 0 }}>Skip tour</button>
          <button onClick={handleNext} style={{ background: '#3b7fff', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            {isLast ? 'Done ✓' : 'Next →'}
          </button>
        </div>
      </div>
    </>
  );
}

export const SecondaryListItems = () => {
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const [openSmartHub, setOpenSmartHub] = useState(true);
  const [showTour, setShowTour] = useState(() => localStorage.getItem(MENU_TOUR_KEY) !== '1');
  const projectCenterRef = useRef(null);
  const smartHubRef = useRef(null);

  // Allow replaying the nav tour on demand (e.g. the header "Tour" button dispatches this).
  useEffect(() => {
    const start = () => { try { localStorage.removeItem(MENU_TOUR_KEY); } catch (_) {} setShowTour(true); };
    window.addEventListener('intelog:replay-menu-tour', start);
    return () => window.removeEventListener('intelog:replay-menu-tour', start);
  }, []);

  const toggleSmartHub = () => {
    setOpenSmartHub(!openSmartHub);
  };

  const isSmartHubActive = location.pathname.startsWith('/smarthub') || 
                          location.pathname.startsWith('/AIAgentHub');

  // Check if user has ANY AI Agent permissions
  const hasAnyAIAgentAccess =
    hasPermission('designAgent') ||
    hasPermission('integrationAgent') ||
    hasPermission('migrationAgent') ||
    hasPermission('migrationDotnet') ||
    hasPermission('migrationJitterbit') ||
    hasPermission('upgradeAgent') ||
    hasPermission('smartHub') ||
    hasPermission('greenfieldAgent') ||
    hasPermission('brownfieldAgent');

  return (
    <div>
      {/* Project Center - Only show if user has permission */}
      {hasPermission('projectCenter') && (
        <a href="/dashboard" ref={projectCenterRef}>
          <ListItem button className={location.pathname === '/dashboard' ? 'menu-active' : 'menu-inactive'}>
            <ListItemIcon>
              <WorkIcon />
            </ListItemIcon>
            <ListItemText primary="Project Center" />
          </ListItem>
        </a>
      )}

      {/* AI Agent Hub Parent - Only show if user has at least one AI Agent permission */}
      {hasAnyAIAgentAccess && (
        <>
          <ListItem 
            button 
            onClick={toggleSmartHub} 
            className={isSmartHubActive ? 'menu-active' : 'menu-inactive'}
          >
            <ListItemIcon>        
                    <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="AI Agent Hub" />
            {openSmartHub ? <ExpandLess /> : <ExpandMore />}
          </ListItem>

          {/* AI Agent Hub Sub-links */}
          <Collapse in={openSmartHub} timeout="auto" unmountOnExit>
            <div className="submenu-container">
                 {/* Design Agent - Only show if user has permission */}
              {/* {hasPermission('designAgent') && (
                <a href="/AIAgentHub/dashboard?agent=Design">
                  <ListItem 
                    button 
                    className={`submenu-item ${location.pathname === '/AIAgentHub/dashboard' && location.search.includes('Design') ? 'menu-active' : 'menu-inactive'}`}
                  >
                    <ListItemIcon>
                      <Palette size={18} />
                    </ListItemIcon>
                    <ListItemText primary="Design Agent" />
                  </ListItem>
                </a>
              )} */}
              
              {/* Integration Agent - Only show if user has permission */}
              {/* {hasPermission('integrationAgent') && (
                <a href="/AIAgentHub/dashboard?agent=Integration">
                  <ListItem 
                    button 
                    className={`submenu-item ${location.pathname === '/AIAgentHub/dashboard' && location.search.includes('Integration') ? 'menu-active' : 'menu-inactive'}`}
                  >
                    <ListItemIcon>
                      <Puzzle size={18} />
                    </ListItemIcon>
                    <ListItemText primary="Integration Agent" />
                  </ListItem>
                </a>
               )}  */}
              
              {/* Migration Agent - Only show if user has permission */}
              {/* {hasPermission('migrationAgent') && (
                <a href="/AIAgentHub/dashboard?agent=Migration">
                  <ListItem 
                    button 
                    className={`submenu-item ${location.pathname === '/AIAgentHub/dashboard' && location.search.includes('Migration') ? 'menu-active' : 'menu-inactive'}`}
                  >
                    <ListItemIcon>
                       <ArrowRightLeft size={18} />
                    </ListItemIcon>
                    <ListItemText primary="Migration Agent" />
                  </ListItem>
                </a>
              )} */}
              
           
              {/* Upgrade Agent */}
              {hasPermission('upgradeAgent') && (
                <a href="/AIAgentHub/UpgradeAgent?agent=Upgrade">
                  <ListItem
                    button
                    className={`submenu-item ${location.pathname === '/AIAgentHub/UpgradeAgent' ? 'menu-active' : 'menu-inactive'}`}
                  >
                    <ListItemIcon>
                      <CircleArrowUp size={18} />
                    </ListItemIcon>
                    <ListItemText primary="Upgrade Agent" />
                  </ListItem>
                </a>
              )}
              
              {/* Deployment Agent - standalone deploy (archive/path → validate → deploy) */}
              {(hasPermission('smartHub') || hasPermission('greenfieldAgent') || hasPermission('brownfieldAgent') || hasPermission('integrationAgent')) && (
                <a href="/AIAgentHub/DeploymentAgent?agent=Deployment">
                  <ListItem
                    button
                    className={`submenu-item ${location.pathname === '/AIAgentHub/DeploymentAgent' ? 'menu-active' : 'menu-inactive'}`}
                  >
                    <ListItemIcon>
                      <Cloud size={18} />
                    </ListItemIcon>
                    <ListItemText primary="Deployment Agent" />
                  </ListItem>
                </a>
              )}

              {/* Policy Upgrade Agent - Show if user has integration permission */}
               {(hasPermission('smartHub') || hasPermission('greenfieldAgent') || hasPermission('brownfieldAgent')) && (
                <a href="/AIAgentHub/smartHub?agent=smartHub" ref={smartHubRef}>
                  <ListItem
                    button
                    className={`submenu-item ${location.pathname === '/AIAgentHub/smartHub' ? 'menu-active' : 'menu-inactive'}`}
                  >
                    <ListItemIcon>
                      <Shield size={18} />
                    </ListItemIcon>
                    <ListItemText primary="Smart Hub" />
                  </ListItem>
                </a>
              )}
            </div>
          </Collapse>
        </>
      )}

      {/* Guided menu tour — shown once to new users */}
      {showTour && (
        <MenuTour
          projectCenterRef={projectCenterRef}
          smartHubRef={smartHubRef}
          onDismiss={() => { setShowTour(false); localStorage.setItem(MENU_TOUR_KEY, '1'); }}
        />
      )}
    </div>
  );
};
